'use client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import DeliveryIcon from '@/fo/icons/base/icons/delivery.svg';
import { UiKitCollapseBase } from '@/fo/ui/kit/collapse/base';
import { UiKitListCharacteristic } from '@/fo/ui/kit/list/characteristic';
import { UiKitPriceBase } from '@/fo/ui/kit/price/base';
import { UiKitProgressLine } from '@/fo/ui/kit/progress/line';
import { utilEventCityModal } from '@/fo/util/event-city-modal';

import { styles } from './UiDeliveryInformerBase.styles';

export interface UiDeliveryInformerBaseProps {
  className?: string;
  subtotalPrice: number;
  targetAmount: number;
  title?: string;
  deliveryCity?: string;
  minDeliveryTime?: string;
  freeShippingByCoupon?: boolean;
  deliveryOptions?: Array<{
    label: string;
    time?: string | null;
    price: number;
  }>;
}

export function UiDeliveryInformerBase({
  className,
  subtotalPrice,
  targetAmount,
  title,
  minDeliveryTime,
  deliveryCity,
  freeShippingByCoupon = false,
  deliveryOptions
}: UiDeliveryInformerBaseProps) {
  const slots = styles();
  const t = useTranslations();
  const [isCollapse, setIsCollapse] = useState(true);

  const isFreeShipping = freeShippingByCoupon || subtotalPrice >= targetAmount;

  return (
    <div className={slots.base({ className })}>
      {title && <p className={slots.title()}>{title}</p>}
      <div className={slots.text()}>
        {!isFreeShipping &&
          subtotalPrice > 0 &&
          subtotalPrice < targetAmount && (
            <>
              <DeliveryIcon className={slots.icon()} />
              {t('Free shipping is still available')}
              <span className={slots.amount()}>
                <UiKitPriceBase value={targetAmount - subtotalPrice} />
              </span>
            </>
          )}
        {!isFreeShipping && subtotalPrice === 0 && (
          <>
            <DeliveryIcon className={slots.icon()} />
            {t('Free shipping when ordering from')}&nbsp;
            <UiKitPriceBase value={targetAmount} />
          </>
        )}
        {isFreeShipping && (
          <>
            <DeliveryIcon className={slots.icon()} />
            {t('Free shipping')}
          </>
        )}
      </div>

      <UiKitProgressLine
        className={slots.line()}
        current={freeShippingByCoupon ? targetAmount : subtotalPrice}
        target={targetAmount}
      />

      {deliveryCity && (
        <div className={slots.info()}>
          <div
            className={slots.trigger()}
            onClick={() => setIsCollapse(!isCollapse)}
          >
            <p>
              {t('Delivery to')}
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  utilEventCityModal.open();
                }}
                className={slots.city()}
              >
                {deliveryCity}
              </span>
              {minDeliveryTime}
            </p>
            <ChevronIcon className={slots.chevron({ active: isCollapse })} />
          </div>

          {deliveryOptions && (
            <UiKitCollapseBase collapse={isCollapse}>
              <UiKitListCharacteristic
                className="w-[450px] max-w-full gap-2"
                items={deliveryOptions.map((option) => ({
                  label: (
                    <p className={slots.characteristicLabel()}>
                      {option.label}
                    </p>
                  ),
                  value: (
                    <div className={slots.characteristicValue()}>
                      <span className={slots.characteristicValueText()}>
                        {option.time}
                      </span>
                      <span className={slots.characteristicValuePrice()}>
                        {isFreeShipping ? (
                          t('Free')
                        ) : (
                          <UiKitPriceBase value={option.price} />
                        )}
                      </span>
                    </div>
                  )
                }))}
              />
            </UiKitCollapseBase>
          )}
        </div>
      )}
    </div>
  );
}
